import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { FileText } from "lucide-react";

export default function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);

  const refresh = useCallback(() => api.get(`/invoices/${id}`).then((r) => setInv(r.data)), [id]);
  useEffect(() => { refresh(); }, [refresh]);

  if (!inv) return <div className="min-h-screen bg-white"><Navbar/><div className="p-12 text-sm text-gray-500">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 md:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium">Invoice</p>
            <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1" data-testid="invoice-number">
              {inv.zoho?.zoho_invoice_number || `#${inv.id.slice(-8)}`}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{new Date(inv.created_at).toLocaleString()}</p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium">Total</div>
            <div className="text-3xl font-heading font-semibold tracking-tight" data-testid="invoice-total">{inr(inv.total)}</div>
          </div>
        </div>

        <div className="border border-gray-200">
          <div className="grid grid-cols-12 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-600 font-medium">
            <div className="col-span-6">Item</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-2 text-right">Unit</div>
            <div className="col-span-2 text-right">Total</div>
          </div>
          {inv.line_items.map((li, i) => (
            <div key={`${li.product_id || li.name}-${i}`} className="grid grid-cols-12 px-4 py-3 border-b border-gray-100 text-sm" data-testid={`line-${i}`}>
              <div className="col-span-6">
                <div className="font-medium">{li.name}</div>
                {li.description && <div className="text-xs text-gray-500 italic">"{li.description}"</div>}
              </div>
              <div className="col-span-2 text-right">{li.quantity}</div>
              <div className="col-span-2 text-right">{inr(li.unit_price)}</div>
              <div className="col-span-2 text-right font-medium">{inr(li.line_total)}</div>
            </div>
          ))}
          <div className="p-4 grid grid-cols-12 text-sm">
            <div className="col-span-8"/>
            <div className="col-span-4 space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{inr(inv.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">GST (18%)</span><span>{inr(inv.tax)}</span></div>
              <div className="flex justify-between font-semibold text-base border-t border-gray-200 pt-1 mt-1"><span>Total</span><span>{inr(inv.total)}</span></div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 flex items-center gap-3 text-sm text-gray-600" data-testid="bill-note">
          <FileText className="w-5 h-5 text-gray-500 shrink-0" strokeWidth={1.5}/>
          <span>This is your bill for the confirmed sales order. For payment, please coordinate with us directly — the order is tracked in Zoho Books.</span>
        </div>
      </div>
    </div>
  );
}
